import { useState } from 'react';
import { Button, Eyebrow, Field, Notice } from '../ui';
import type { ProblemCopy } from '../api/problem';
import { describeWriteProblem, privacyDetail, privacyLabel, titleFieldState } from './model';
import { rememberPlaylistPrivacy } from './privacyMemory';
import { useCopy } from '../strings/useCopy';
import { useCreatePlaylist } from './mutations';
import styles from './createPlaylist.module.css';

const PRIVACIES = ['public', 'unlisted', 'private'] as const;
type Privacy = (typeof PRIVACIES)[number];

/**
 * The compact create form the modals share: it lets the add-to-playlist modal offer to create a
 * new playlist, built once because the copy/move dialog needs the identical thing.
 *
 * It differs from the Playlists page's create form in what happens *after* the `201`: that page
 * navigates to the new playlist, while a modal's caller has a video in hand and wants the id —
 * `onCreated` — to keep going (add the video, copy the selection) without the reader leaving the
 * dialog. What is the same is deliberately the same code: `titleFieldState` (the 150-**byte** cap,
 * checked before the wire), the segmented privacy control, and `rememberPlaylistPrivacy`, because
 * `privacy` is write-only and this is the moment the app knows it exactly.
 */
export function CreatePlaylistForm({
  id,
  submitLabel,
  onCreated,
}: {
  /** Uniquifies the field ids — two mounted forms must not share a label target. */
  readonly id: string;
  /** What the button promises. The add dialog says it saves the video too; the default is "Create". */
  readonly submitLabel?: string;
  /** The title rides along for the caller that snapshots it (the Watch later picker). */
  readonly onCreated: (playlistId: string, title: string) => void;
}) {
  const words = useCopy().text.playlists.page.create;
  const { create, creating } = useCreatePlaylist();
  const [title, setTitle] = useState('');
  const [privacy, setPrivacy] = useState<Privacy>('private');
  const [problem, setProblem] = useState<ProblemCopy | null>(null);

  const field = titleFieldState(title);
  const detail = privacyDetail();

  const submit = (): void => {
    const value = field.value;
    if (value === null || creating) return;
    const chosen = privacy;
    void (async () => {
      setProblem(null);
      const result = await create({ title: value, privacy: chosen });
      if (!result.ok) {
        setProblem(describeWriteProblem(result.problem, 'create'));
        return;
      }
      setTitle('');
      // Exact, because we are the one who sent it — no read will ever report `privacy` back.
      rememberPlaylistPrivacy(result.value.playlistId, chosen);
      onCreated(result.value.playlistId, result.value.title);
    })();
  };

  return (
    <section className={styles.create} aria-label={words.sectionLabel}>
      <Eyebrow>{words.heading}</Eyebrow>
      <div className={styles.row}>
        <Field
          id={`${id}-title`}
          label={words.titleLabel}
          hideLabel
          value={title}
          onChange={(next) => {
            setTitle(next);
            // A refusal is about the value that was sent, so it stops being true when the value changes.
            if (problem !== null) setProblem(null);
          }}
          onSubmit={submit}
          placeholder={words.titlePlaceholder}
          problem={field.problem}
          hint={field.hint}
          className={styles.titleField}
        />
        <div
          className={styles.privacyGroup}
          role="radiogroup"
          aria-label={words.privacyLabel}
          title={detail[privacy]}
        >
          {PRIVACIES.map((option) => (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={privacy === option}
              className={styles.privacyOption}
              title={detail[option]}
              onClick={() => setPrivacy(option)}
            >
              {privacyLabel()[option]}
            </button>
          ))}
        </div>
      </div>
      {/* Always its own row: wrapped in beside the privacy control, the submit read as one
          of its segments, and where it landed depended on how narrow the dialog happened to be. */}
      <div className={styles.actions}>
        <Button
          variant="primary"
          size="sm"
          onClick={submit}
          disabled={field.value === null || creating}
        >
          {creating ? words.submitting : (submitLabel ?? words.submit)}
        </Button>
      </div>
      {problem === null ? null : (
        <Notice tone="caution" live>
          <strong>{problem.title}.</strong> {problem.detail}
        </Notice>
      )}
    </section>
  );
}
